// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { LanguageSelector } from '../i18n';
import { useVerticals, buildVerticalNavItems, getDemoVerticals } from '../verticals/useVerticals';
import { VERTICAL_AWARE_ROLES } from '../verticals/verticals-config';
import useTextSize from '../hooks/useTextSize';
import logoImg from '../assets/logo.png';


const NAV_ITEMS = {
  owner: [
    { section: 'Command' },
    { path: '/overwatch', icon: '👁', label: 'Overwatch' },
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/schedule', icon: '🗓', label: 'Schedule' },
    { path: '/time-clock', icon: '⏱', label: 'Time Clock' },
    { section: 'Operations' },
    { path: '/jobs', icon: '📋', label: 'Jobs' },
    { path: '/jobs/new', icon: '➕', label: 'Create Job' },
    { path: '/customers', icon: '👥', label: 'Customers' },
    { path: '/vendors', icon: '📦', label: 'Vendors' },
    { path: '/onboarding', icon: '🚀', label: 'Onboarding' },
    { section: '__VERTICALS__' },
    { section: 'Deliveries' },
    { path: '/delivery-permits', icon: '🚚', label: 'Delivery Permits' },
    { section: 'Financial' },
    { path: '/quotes', icon: '💬', label: 'Quotes' },
    { path: '/invoices', icon: '🧾', label: 'Invoices' },
    { path: '/analytics', icon: '📈', label: 'Analytics' },
    { section: 'Dashboards' },
    { path: '/reception', icon: '📞', label: 'Reception' },
    { path: '/view/owner', icon: '👑', label: 'Owner' },
    { path: '/view/accounting', icon: '💰', label: 'Accounting' },
    { path: '/view/dispatcher', icon: '📡', label: 'Dispatch' },
    { path: '/view/technician', icon: '🔧', label: 'Technician' },
    { path: '/view/cfo', icon: '📊', label: 'CFO' },
    { path: '/test-reports', icon: '🧪', label: 'Test Reports' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { section: 'System' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/notifications', icon: '🔔', label: 'Activity' },
    { path: '/it-admin', icon: '🖥', label: 'IT Admin' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
  founder: [
    { path: '/founder', icon: '🏠', label: 'My Dashboard' },
    { path: '/time-clock', icon: '⏱', label: 'Time Clock' },
    { path: '/jobs', icon: '📋', label: 'All Jobs' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/profile', icon: '👤', label: 'My Account' },
  ],
  dispatcher: [
    { section: 'Operations' },
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/schedule', icon: '🗓', label: 'Schedule' },
    { path: '/time-clock', icon: '⏱', label: 'Time Clock' },
    { path: '/jobs', icon: '📋', label: 'Jobs' },
    { path: '/jobs/new', icon: '➕', label: 'Create Job' },
    { path: '/quotes', icon: '💬', label: 'Quotes' },
    { path: '/customers', icon: '👥', label: 'Customers' },
    { path: '/vendors', icon: '📦', label: 'Vendors' },
    { section: '__VERTICALS__' },
    { section: 'Deliveries' },
    { path: '/delivery-permits', icon: '🚚', label: 'Delivery Permits' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { section: 'System' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/notifications', icon: '🔔', label: 'Activity' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
  technician: [
    { section: 'My Work' },
    { path: '/time-clock', icon: '⏱', label: 'Time Clock' },
    { path: '/jobs', icon: '📋', label: 'My Jobs' },
    { path: '/sops', icon: '📖', label: 'Door SOPs' },
    { path: '/vendors', icon: '📦', label: 'Vendors' },
    { section: '__VERTICALS__' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { section: 'System' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/notifications', icon: '🔔', label: 'Activity' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
  accounting: [
    { section: 'Financial' },
    { path: '/quotes', icon: '💬', label: 'Quote Review' },
    { path: '/invoices', icon: '🧾', label: 'Invoices' },
    { path: '/analytics', icon: '📈', label: 'Analytics' },
    { section: 'Operations' },
    { path: '/jobs', icon: '📋', label: 'Jobs' },
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { section: 'System' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
  cfo: [
    { section: 'Financial Command' },
    { path: '/analytics', icon: '📈', label: 'Financial Analytics' },
    { path: '/invoices', icon: '🧾', label: 'Invoices & Revenue' },
    { path: '/quotes', icon: '💬', label: 'Quote Pipeline' },
    { section: 'Operations Intel' },
    { path: '/overwatch', icon: '👁', label: 'Overwatch' },
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/jobs', icon: '📋', label: 'Jobs' },
    { path: '/customers', icon: '👥', label: 'Customers' },
    { path: '/vendors', icon: '📦', label: 'Vendors' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { section: 'System' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/notifications', icon: '🔔', label: 'Activity' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
  receptionist: [
    { path: '/reception', icon: '🏠', label: 'My Dashboard' },
    { path: '/dashboard', icon: '📊', label: 'Dispatch Board' },
    { path: '/schedule', icon: '🗓', label: 'Schedule' },
    { path: '/jobs/new', icon: '➕', label: 'New Job' },
    { path: '/quotes', icon: '💬', label: 'Quotes' },
    { path: '/scanner', icon: '📷', label: 'Document Scanner' },
    { path: '/customers', icon: '👥', label: 'Customers' },
    { path: '/vendors', icon: '📦', label: 'Vendors' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { path: '/assistant', icon: '🤖', label: 'AI Assistant' },
    { path: '/profile', icon: '👤', label: 'My Account' },
  ],
  demo: [
    { section: 'Overview' },
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/analytics', icon: '📈', label: 'Analytics' },
    { section: 'Recording' },
    { path: '/site-recordings', icon: '🎙', label: 'Site Recordings' },
    { path: '/e-signature-consent', icon: '✍️', label: 'E-Signature Consent' },
    { path: '/transcriptions', icon: '📝', label: 'Transcriptions' },
    { path: '/profile', icon: '👤', label: 'Profile' },
  ],
};

// ═══════════════════════════════════════════════════════════════
// IOD ACCESS: ONLY Island Overhead Doors employees + Ryan + Jordan
// Tyler Hodgson is an employee of BOTH IOD and Mt Forge
// ═══════════════════════════════════════════════════════════════
function getEffectiveRole(user) {
  if (!user) return 'technician';
  const email = (user.email || '').toLowerCase();
  // ── Harold Blahut — IOD Founder ──
  if (email === 'harold@islandoverheaddoors.com') return 'founder';
  if (email === 'haroldblahut@gmail.com') return 'founder';
  if (email === 'h.blahut@miskulintechnologies.com') return 'founder';
  // ── Suzanne — IOD Receptionist ──
  if (email === 'info@islandoverheaddoors.com') return 'receptionist';
  // ── Bobbi — IOD Dispatcher ──
  if (email === 'bobbi@islandoverheaddoors.com') return 'dispatcher';
  // ── Clorice — IOD Accounting ──
  if (email === 'accounting@islandoverheaddoors.com') return 'accounting';
  if (email === 'cloricer77@gmail.com') return 'accounting';
  if (email === 'c.robertson@miskulintechnologies.com') return 'accounting';
  // ── Tyler Hodgson — IOD + Mt Forge employee (owner-level access) ──
  if (email === 'tyler.hodgson32@gmail.com') return 'owner';
  if (email === 'threewayindustries@gmail.com') return 'owner';
  if (email === 't.hodgson@miskulintechnologies.com') return 'owner';
  // ── Ryan Miskulin — Mt Forge CEO (owner-level access) ──
  if (email === 'rmiskulin@gmail.com') return 'owner';
  if (email === 'r.miskulin@miskulintechnologies.com') return 'owner';
  // ── Jordan Lutz — Mt Forge Chief Systems Architect (owner-level access) ──
  if (email === 'jordan.l.lutz@gmail.com') return 'owner';
  if (email === 'j.lutz@miskulintechnologies.com') return 'owner';
  if (email === 'criodtech@gmail.com') return 'owner';
  // ── Demo account ──
  if (email === 'demo@iqapps.dev') return 'demo';
  // Default
  return user.role || 'technician';
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewAsRole, setViewAsRole] = useState(null);
  const { size: textSize, setSize: setTextSize, SIZES: TEXT_SIZES } = useTextSize();
  const actualRole = getEffectiveRole(user);
  const role = viewAsRole || actualRole;

  // Close sidebar on any route change (fixes mobile sidebar stuck open)
  const location = useLocation();
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);
  // IOD AUTHORIZED USERS ONLY — IOD employees + Ryan + Jordan
  const ADMIN_TEAM = [
    // IOD Employees
    "harold@islandoverheaddoors.com",
    "haroldblahut@gmail.com",
    "H.blahut@miskulintechnologies.com",
    "info@islandoverheaddoors.com",
    "bobbi@islandoverheaddoors.com",
    "accounting@islandoverheaddoors.com",
    "cloricer77@gmail.com",
    "C.robertson@miskulintechnologies.com",
    // Tyler Hodgson (employee of BOTH IOD and Mt Forge)
    "tyler.hodgson32@gmail.com",
    "threewayindustries@gmail.com",
    "T.hodgson@miskulintechnologies.com",
    // Ryan Miskulin (CEO)
    "rmiskulin@gmail.com",
    "R.miskulin@miskulintechnologies.com",
    // Jordan Lutz (Chief Systems Architect)
    "jordan.l.lutz@gmail.com",
    "J.lutz@miskulintechnologies.com",
    "criodtech@gmail.com",
    // Demo
    "demo@iqapps.dev",
    // Mt Forge team
    "j.lutz@mtforge.ai",
    "r.miskulin@mtforge.ai",
    "t.schenher@mtforge.ai",
    "c.leblanc@mtforge.ai",
    "d.bachynski@mtforge.ai",
  ];
  const OVERWATCH_TEAM = ['J.lutz@miskulintechnologies.com', 'R.miskulin@miskulintechnologies.com', 'tyler.hodgson32@gmail.com', 'threewayindustries@gmail.com'];
  const isAdmin = ADMIN_TEAM.map(e => e.toLowerCase()).includes((user?.email || '').toLowerCase());
  const displayRole = isAdmin
    ? 'IOD Admin'
    : role === 'receptionist' ? 'Reception' : role;
  // Get selected verticals
  const { selected: selectedVerticals } = useVerticals();
  const activeVerticals = role === 'demo' ? getDemoVerticals() : selectedVerticals;

  // Build nav: start with base items, then inject vertical nav at __VERTICALS__ placeholder
  let navItems = NAV_ITEMS[role] || NAV_ITEMS.owner;
  if (VERTICAL_AWARE_ROLES.includes(role)) {
    const verticalNav = buildVerticalNavItems(role, activeVerticals);
    const result = [];
    navItems.forEach(item => {
      if (item.section === '__VERTICALS__') {
        result.push(...verticalNav);
      } else {
        result.push(item);
      }
    });
    navItems = result;
  }
  if (!OVERWATCH_TEAM.map(e => e.toLowerCase()).includes((user?.email || '').toLowerCase())) {
    navItems = navItems.filter(item => item.path !== '/overwatch');
  }
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('') || 'U';
  const companyLabel = isAdmin ? 'Island Overhead Doors' : (user?.company?.company_name || 'Island Overhead Doors');
  const isCIQ = isAdmin;

  return (
    <div className="app-layout">
      {/* Mobile hamburger — always visible, always closeable */}
      <button
        className="mobile-menu-btn"
        onClick={(e) => { e.stopPropagation(); setSidebarOpen(prev => !prev); }}
        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setSidebarOpen(prev => !prev); }}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        style={{ zIndex: sidebarOpen ? 1000 : 150, touchAction: 'manipulation' }}
      >
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          onTouchEnd={(e) => { e.preventDefault(); setSidebarOpen(false); }}
          style={{ zIndex: 140 }}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logoImg} alt="Island Overhead Doors" style={{ height: 36, marginRight: 8, borderRadius: 6 }} />
            <div>
              <div className="sidebar-logo">Island Overhead Doors</div>
              <div className="sidebar-sub" style={isCIQ ? {
                background: 'linear-gradient(135deg, #8B5CF6, #3B82F6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 0.5,
              } : {}}>
                {companyLabel}
              </div>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="sidebar-section">{item.section}</div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            )
          )}
        {actualRole === 'owner' && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>
              View As Role
            </div>
            {Object.keys(NAV_ITEMS).filter(r => r !== 'owner' && r !== 'demo' && r !== 'founder').map(r => (
              <button key={r} onClick={() => setViewAsRole(viewAsRole === r ? null : r)}
                style={{
                  display: 'block', width: '100%', padding: '6px 10px', marginBottom: 4,
                  background: viewAsRole === r ? '#4F46E5' : 'rgba(255,255,255,0.05)',
                  color: viewAsRole === r ? '#fff' : '#94a3b8',
                  border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  textAlign: 'left', textTransform: 'capitalize'
                }}
              >
                {viewAsRole === r ? '\u2713 ' : ''}{r.replace(/_/g, ' ')}
              </button>
            ))}
            {viewAsRole && (
              <button onClick={() => setViewAsRole(null)}
                style={{
                  display: 'block', width: '100%', padding: '6px 10px', marginTop: 8,
                  background: '#dc2626', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  textAlign: 'center', fontWeight: 600
                }}
              >
                {'\u2190'} Back to Owner
              </button>
            )}
          </div>
        )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{initials}</div>
            <div>
              <div className="user-name">{user?.full_name}</div>
              <div className="user-role">{viewAsRole ? `Viewing as: ${viewAsRole.replace(/_/g, ' ')}` : displayRole}</div>
            </div>
          </div>
          {/* Text Size Picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, width: '100%' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>Text:</span>
            {TEXT_SIZES.map(s => (
              <button
                key={s.value}
                onClick={() => setTextSize(s.value)}
                style={{
                  flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 700,
                  border: textSize === s.value ? '2px solid #3B82F6' : '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6, cursor: 'pointer',
                  background: textSize === s.value ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.05)',
                  color: textSize === s.value ? '#3B82F6' : '#94a3b8',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          <LanguageSelector style={{ width: '100%', marginTop: 8 }} />
          <button
            className="btn btn-outline btn-sm"
            style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}
            onClick={() => { signOut(); navigate('/login'); }}
          >
            Sign Out
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
